import { type FormEvent, useCallback, useState } from 'react';

import { submitContactForm } from '../services/contactSubmissionService';
import type {
  ContactFormErrors,
  ContactFormValues,
  FormStatus,
} from '../types/contactTypes';
import { validateContactForm } from '../utils/contactUtils';

const INITIAL_VALUES: ContactFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  message: '',
};

export function useContactForm() {
  const [values, setValues] = useState<ContactFormValues>(INITIAL_VALUES);
  const [fieldErrors, setFieldErrors] = useState<ContactFormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setFieldValue = useCallback(
    (name: keyof ContactFormValues, value: string) => {
      setValues((prev) => ({ ...prev, [name]: value }));
      if (fieldErrors[name]) {
        setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    },
    [fieldErrors],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setErrorMessage(null);
      setStatus('loading');

      const errors = validateContactForm(values);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setStatus('idle');
        return;
      }

      try {
        await submitContactForm(values);
        setStatus('success');
        setValues(INITIAL_VALUES);
      } catch (err) {
        setErrorMessage(
          err instanceof Error ? err.message : 'Failed to send message',
        );
        setStatus('error');
      }
    },
    [values],
  );

  return {
    values,
    fieldErrors,
    status,
    errorMessage,
    setFieldValue,
    handleSubmit,
  } as const;
}
